import { ApolloError, useLazyQuery } from '@apollo/client';
import React, { useContext, useEffect, useMemo } from 'react';
import { GET_VOUCHERS } from '../queries';
import { IGetVouchersResult } from '@monofront/types';
import invariant from 'tiny-invariant';
import { useReferrerDetails } from '../hooks';
import { logger } from '@monofront/logging';

type ReferralReferrerContextType = {
  vouchersData: IGetVouchersResult | null;
  vouchersLoading: boolean;
  vouchersError: ApolloError | undefined;
  fetchVouchers: () => void;
  isReferralLoading: boolean;
  referralLink: string | undefined;
  referrerCode: string | undefined;
};

const ReferralReferrerContext =
  React.createContext<ReferralReferrerContextType | null>(null);

export const ReferralReferrerProvider = ({
  baseUrl,
  children,
}: {
  baseUrl: string;
  children: React.ReactNode;
}) => {
  const {
    isLoading: isReferralLoading,
    referralLink,
    referrerCode,
  } = useReferrerDetails(baseUrl);
  const [
    fetchVouchers,
    { data: getVouchersData, loading: isGetVouchersLoading, error },
  ] = useLazyQuery<IGetVouchersResult>(GET_VOUCHERS, {
    ssr: false,
    fetchPolicy: 'cache-first',
  });

  return (
    <ReferralReferrerContext.Provider
      value={useMemo(
        () => ({
          vouchersData: getVouchersData ?? null,
          vouchersLoading: isGetVouchersLoading,
          vouchersError: error,
          fetchVouchers,
          isReferralLoading,
          referralLink,
          referrerCode,
        }),
        [
          getVouchersData,
          isGetVouchersLoading,
          isReferralLoading,
          referralLink,
          referrerCode,
          error,
        ],
      )}
    >
      {children}
    </ReferralReferrerContext.Provider>
  );
};

export const useReferralReferrer = () => {
  const context = useContext(ReferralReferrerContext);

  invariant(context, 'A ReferralReferrerProvider must be present in the tree');

  useEffect(() => {
    if (context.vouchersError) {
      logger.error('Errors while fetching vouchers', context.vouchersError);
    }

    if (
      !context.vouchersData &&
      !context.vouchersLoading &&
      !context.vouchersError
    ) {
      context.fetchVouchers();
    }
  }, [context]);

  return context;
};
